// src/pages/PropertyContacts/PropertyContactsPage.tsx
import React, { useState, useCallback } from "react";
import {
  Plus,
  Phone,
  Edit,
  Trash2,
  Eye,
  Clock,
  Calendar,
} from "lucide-react";
import {
  DataTable,
  PageHeader,
  LoadingSpinner,
  EmptyState,
} from "../../components/common";
import type {
  DataTableColumn,
  DataTableAction,
  DataTableBulkAction,
} from "../../components/common";
import {
  useGetPropertyContactsQuery,
  useDeletePropertyContactMutation,
} from "../../store/api/propertyContactApi";
import { useTable } from "../../hooks/common/useTable";
import { useToast } from "../../hooks/common/useToast";
import { formatDate } from "../../utils/formatters";
import type {
  PropertyContactResponseDto,
  PropertyContactFilterDto,
} from "../../types/property-contact";
import PropertyContactFormModal from "./components/PropertyContactFormModal";
import PropertyContactDetailModal from "./components/PropertyContactDetailModal";

// Stats Card Component
const StatsCard: React.FC<{
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}> = ({ title, value, icon, color }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
    <div className="flex items-center">
      <div className={`p-2 rounded-lg ${color}`}>{icon}</div>
      <div className="ml-4">
        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
          {title}
        </p>
        <p className="text-2xl font-semibold text-gray-900 dark:text-white">
          {value.toLocaleString()}
        </p>
      </div>
    </div>
  </div>
);

const PropertyContactsPage: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<PropertyContactResponseDto | null>(null);

  const { showToast } = useToast();
  const {
    currentPage,
    pageSize,
    sortConfig,
    filters,
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    handleFilterChange,
    getQueryParams,
  } = useTable({
    key: "property-contacts",
    defaultPageSize: 25,
  });

  // Build query parameters
  const queryParams = {
    ...getQueryParams(),
    ...(sortConfig && {
      sortBy: sortConfig.key,
      sortOrder: sortConfig.direction?.toUpperCase() as "ASC" | "DESC",
    }),
  };

  // API Queries
  const {
    data: contactsData,
    isLoading,
    error,
    refetch,
  } = useGetPropertyContactsQuery(queryParams as PropertyContactFilterDto);

  const [deleteContact] = useDeletePropertyContactMutation();

  const contacts = contactsData?.data || [];
  const totalRecords = contactsData?.pagination?.total || 0;

  const handleView = useCallback((contact: PropertyContactResponseDto) => {
    setSelectedContact(contact);
    setIsDetailModalOpen(true);
  }, []);

  const handleEdit = useCallback((contact: PropertyContactResponseDto) => {
    setSelectedContact(contact);
    setIsEditModalOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (contact: PropertyContactResponseDto) => {
      try {
        await deleteContact(contact.id).unwrap();
        showToast("success", "Property contact deleted successfully");
        refetch();
      } catch (error) {
        showToast("error", "Failed to delete property contact");
      }
    },
    [deleteContact, showToast, refetch]
  );

  // Table Columns
  const columns: DataTableColumn<PropertyContactResponseDto>[] = [
    {
      key: "id",
      title: "ID",
      sortable: true,
      width: "80px",
      render: (value) => `#${value}`,
    },
    {
      key: "phone",
      title: "Phone Number",
      sortable: true,
      render: (value, record) => (
        <div className="flex items-center">
          <Phone className="h-4 w-4 text-gray-400 mr-2" />
          <span className="font-medium">{String(value)}</span>
        </div>
      ),
    },
    {
      key: "createdAt",
      title: "Created",
      sortable: true,
      render: (value) => (
        <div className="flex items-center text-sm text-gray-600">
          <Calendar className="h-4 w-4 mr-1" />
          {formatDate(value as Date)}
        </div>
      ),
    },
    {
      key: "updatedAt",
      title: "Last Updated",
      sortable: true,
      render: (value) => (
        <div className="flex items-center text-sm text-gray-600">
          <Clock className="h-4 w-4 mr-1" />
          {formatDate(value as Date)}
        </div>
      ),
    },
  ];

  // Table Actions
  const actions: DataTableAction<PropertyContactResponseDto>[] = [
    {
      key: "view",
      label: "View",
      icon: <Eye className="h-4 w-4" />,
      onClick: handleView,
      variant: "ghost",
    },
    {
      key: "edit",
      label: "Edit",
      icon: <Edit className="h-4 w-4" />,
      onClick: handleEdit,
      variant: "ghost",
    },
    {
      key: "delete",
      label: "Delete",
      icon: <Trash2 className="h-4 w-4" />,
      onClick: handleDelete,
      variant: "danger",
    },
  ];

  // Bulk Actions
  const bulkActions: DataTableBulkAction<PropertyContactResponseDto>[] = [
    {
      key: "delete",
      label: "Delete Selected",
      icon: <Trash2 className="h-4 w-4" />,
      variant: "danger",
      onClick: async (selectedItems) => {
        // TODO: Implement bulk delete
        console.log("Bulk delete:", selectedItems);
      },
    },
  ];

  // Modal Handlers
  const handleCreateModalClose = () => {
    setIsCreateModalOpen(false);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedContact(null);
  };

  const handleDetailModalClose = () => {
    setIsDetailModalOpen(false);
    setSelectedContact(null);
  };

  const handleCreateSuccess = () => {
    refetch();
    handleCreateModalClose();
    showToast("success", "Property contact created successfully");
  };

  const handleEditSuccess = () => {
    refetch();
    handleEditModalClose();
    showToast("success", "Property contact updated successfully");
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Error loading property contacts
          </h3>
          <p className="text-gray-600 mb-4">
            There was an error loading the property contacts data.
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Property Contacts"
        action={{
          label: "Add Contact",
          icon: <Plus className="h-4 w-4" />,
          onClick: () => setIsCreateModalOpen(true),
        }}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          title="Total Contacts"
          value={totalRecords}
          icon={<Phone className="h-5 w-5" />}
          color="bg-blue-100 text-blue-600"
        />
        <StatsCard
          title="Today"
          value={0} // You can add this to your backend stats
          icon={<Calendar className="h-5 w-5" />}
          color="bg-green-100 text-green-600"
        />
        <StatsCard
          title="This Week"
          value={0} // You can add this to your backend stats
          icon={<Clock className="h-5 w-5" />}
          color="bg-yellow-100 text-yellow-600"
        />
        <StatsCard
          title="This Month"
          value={0} // You can add this to your backend stats
          icon={<Calendar className="h-5 w-5" />}
          color="bg-purple-100 text-purple-600"
        />
      </div>

      {/* Data Table */}
      {isLoading ? (
        <LoadingSpinner />
      ) : contacts.length === 0 ? (
        <EmptyState
          icon={<Phone className="h-12 w-12" />}
          title="No property contacts found"
          description="Get started by creating your first property contact."
          action={{
            label: "Add Contact",
            onClick: () => setIsCreateModalOpen(true),
          }}
        />
      ) : (
        <DataTable<PropertyContactResponseDto>
          data={contacts}
          columns={columns}
          loading={isLoading}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize,
            total: totalRecords,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: true,
            onChange: handlePageChange,
          }}
          sortable
          onSort={(sort) => {
            if (sort) {
              handleSort(sort.field);
            }
          }}
          searchable
          searchPlaceholder="Search by phone number..."
          onSearch={(value) => handleFilterChange("search", value)}
          actions={actions}
        />
      )}

      {/* Modals */}
      <PropertyContactFormModal
        isOpen={isCreateModalOpen}
        onClose={handleCreateModalClose}
        onSuccess={handleCreateSuccess}
        title="Add Property Contact"
      />

      <PropertyContactFormModal
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        onSuccess={handleEditSuccess}
        title="Edit Property Contact"
        contact={selectedContact}
      />

      <PropertyContactDetailModal
        isOpen={isDetailModalOpen}
        onClose={handleDetailModalClose}
        contact={selectedContact}
      />
    </div>
  );
};

export default PropertyContactsPage;